import { jobPaymentMeta, normaliseJobStatus } from './utils'

export const LOYALTY_CATEGORY_OPTIONS = [
  ['fork', 'Fork'],
  ['shock', 'Shock'],
  ['combined', 'Combined fork/shock'],
  ['dropper', 'Dropper'],
  ['brake', 'Brake'],
  ['other', 'Other'],
]

export const DEFAULT_LOYALTY_CATEGORIES = ['fork', 'shock', 'combined']

export const LOYALTY_WORKSHOP_COLUMNS = `
  loyalty_enabled,
  loyalty_required_completed_services,
  loyalty_reward_type,
  loyalty_reward_value,
  loyalty_eligible_categories,
  discount_approval_required
`

const VALID_CATEGORIES = new Set(LOYALTY_CATEGORY_OPTIONS.map(([key]) => key))

function normaliseArray(value) {
  if (!value) return []
  if (Array.isArray(value)) return value
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function normaliseObject(value) {
  if (!value) return {}
  if (typeof value === 'object' && !Array.isArray(value)) return value
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

export function normaliseLoyaltyCategories(value) {
  const categories = normaliseArray(value)
    .map(category => String(category || '').trim().toLowerCase())
    .filter(category => VALID_CATEGORIES.has(category))
  return categories.length ? [...new Set(categories)] : [...DEFAULT_LOYALTY_CATEGORIES]
}

export function loyaltyRewardLabel(workshop = {}) {
  const value = Number(workshop.loyalty_reward_value || 0)
  if (workshop.loyalty_reward_type === 'fixed') return `GBP ${value.toFixed(2)} off`
  return `${value.toFixed(value % 1 === 0 ? 0 : 2)}% off`
}

export function loyaltyRuleSummary(workshop = {}) {
  const required = Math.max(1, Number(workshop.loyalty_required_completed_services || 4))
  return `${required} completed paid eligible service${required === 1 ? '' : 's'} earns ${loyaltyRewardLabel(workshop)}`
}

function addCategory(set, category) {
  const key = String(category || '').trim().toLowerCase()
  if (VALID_CATEGORIES.has(key)) set.add(key)
}

function inferCategoriesFromText(text, set) {
  const value = String(text || '').toLowerCase()
  if (/(fork|zeb|pike|lyrik|sid|boxxer|domain|reba|recon|36|38|40)/.test(value)) addCategory(set, 'fork')
  if (/(shock|super deluxe|deluxe|vivid|float x|dhx|coil|rear)/.test(value)) addCategory(set, 'shock')
  if (/(dropper|reverb|seatpost)/.test(value)) addCategory(set, 'dropper')
  if (/(brake|bleed|caliper|lever|rotor|pad|hose|maven|code|guide|level)/.test(value)) addCategory(set, 'brake')
}

export function loyaltyJobCategories(job = {}, component = null) {
  const categories = new Set()
  addCategory(categories, component?.type)

  const snapshot = normaliseObject(job.service_template_snapshot)
  addCategory(categories, snapshot.component_type)
  addCategory(categories, snapshot.service_category)

  normaliseArray(job.service_template_addon_snapshots).forEach(addon => {
    addCategory(categories, addon?.component_type)
    addCategory(categories, addon?.service_category)
  })

  inferCategoriesFromText(job.service_summary, categories)

  if (categories.has('fork') && categories.has('shock')) addCategory(categories, 'combined')
  return [...categories]
}

export function jobMatchesLoyaltyCategories(job = {}, component = null, categories = DEFAULT_LOYALTY_CATEGORIES) {
  const eligible = new Set(normaliseLoyaltyCategories(categories))
  return loyaltyJobCategories(job, component).some(category => eligible.has(category))
}

export function jobIsCompletedAndPaid(job = {}) {
  return normaliseJobStatus(job.status) === 'completed' && jobPaymentMeta(job).key === 'paid'
}

export function jobQualifiesForLoyaltyProgress(job = {}, component = null, categories = DEFAULT_LOYALTY_CATEGORIES) {
  return jobIsCompletedAndPaid(job) && jobMatchesLoyaltyCategories(job, component, categories)
}

export function jobCanReceiveLoyaltyReward(job = {}, component = null, workshop = {}) {
  if (workshop?.loyalty_enabled !== true) return false
  if (normaliseJobStatus(job.status) === 'cancelled') return false
  if (jobPaymentMeta(job).key === 'paid') return false
  return jobMatchesLoyaltyCategories(job, component, workshop.loyalty_eligible_categories)
}

export function loyaltyProgressForCustomer({ workshop = {}, jobs = [], components = [], rewards = [], customerId = '' }) {
  const enabled = workshop.loyalty_enabled === true
  const required = Math.max(1, Number(workshop.loyalty_required_completed_services || 4))
  const categories = normaliseLoyaltyCategories(workshop.loyalty_eligible_categories)
  const componentById = new Map((components || []).map(component => [component.id, component]))
  const qualifiedJobs = enabled
    ? (jobs || []).filter(job => {
        if (customerId && job.customer_id !== customerId) return false
        return jobQualifiesForLoyaltyProgress(job, componentById.get(job.component_id), categories)
      })
    : []
  const appliedCount = enabled
    ? (rewards || []).filter(reward => {
        if (customerId && reward.customer_id !== customerId) return false
        return reward.status === 'applied'
      }).length
    : 0
  const earnedCount = Math.floor(qualifiedJobs.length / required)
  const availableCount = Math.max(0, earnedCount - appliedCount)
  const remainder = qualifiedJobs.length % required
  const nextNeeded = availableCount > 0 ? 0 : (remainder === 0 ? required : required - remainder)

  return {
    enabled,
    required,
    categories,
    qualifiedCount: qualifiedJobs.length,
    qualifiedJobs,
    earnedCount,
    appliedCount,
    availableCount,
    nextNeeded,
    rewardLabel: loyaltyRewardLabel(workshop),
    summary: loyaltyRuleSummary(workshop),
  }
}
